/**
 * kp-sibling-row — swap the product page to a sibling design in place.
 *
 * Kyperus sells several cues that are the same physical cue with different
 * artwork. The row at the foot of the buy box lists every design in the series;
 * clicking one changes the page to that product without a navigation.
 *
 * THIS FILE ADDS A TRIGGER AND NOTHING ELSE. Dawn 16 already ships the whole
 * client half of Combined Listings — product-info.js fetches the sibling's
 * entire page, replaces <main> through a view transition, copies the document
 * title and rewrites history. It is dormant only because the Liquid property
 * that supplies the target URL (`option_value.product_url`) is Plus-only and
 * returns nil on this plan. We publish the event Dawn is already listening
 * for, with the card as the target and an EMPTY selected-option-values array,
 * and Dawn does the rest. See:
 *   docs/adr/0003-kp-owned-code-publishes-into-dawns-option-value-selection-channel.md
 *
 * The empty array is not a shortcut. Option value ids are per-product, so
 * forwarding this cue's ids to a sibling's URL yields a null selected variant
 * every time — the swapped-in page would arrive with no price, no SKU and a
 * dead buy button.
 *
 * WHAT DAWN DOES NOT DO, AND THIS FILE THEREFORE DOES:
 *   - scrolls to the gallery, so the shopper sees the cue they asked for
 *   - lands focus on the selected card with the browser's own scroll-into-view
 *     suppressed, so it does not fight the gallery scroll
 *   - scrolls the row horizontally to the selected card, which the rebuilt row
 *     would otherwise have scrolled off the edge
 *   - announces the new design in a polite live region
 *   - patches canonical and OpenGraph in <head>, which Dawn leaves describing
 *     the product the shopper started on
 *
 * NOTHING HERE MAY CACHE AN ELEMENT REFERENCE ACROSS A SWAP. Dawn's
 * HTMLUpdateUtility.viewTransition renames every id on the outgoing DOM and
 * keeps both alive for 500ms, so a held reference is a detached node with a
 * mangled id. State that must survive a swap lives in the module-level
 * `kpPendingSwap` object below, and it holds strings only.
 */

/* Everything below is inside a guarded IIFE, and that is not style.
 *
 * Dawn's HTMLUpdateUtility.setInnerHTML RE-INJECTS every <script> tag in the
 * swapped-in DOM, so this file is fetched and executed again on every swap.
 * Top-level `const` and `let` in a classic script share the global lexical
 * scope, so the second run threw
 *
 *   Identifier 'KP_SIBLING_LOADER_DELAY' has already been declared
 *
 * on every single swap. Nothing visibly broke — but only because the thrown
 * SyntaxError aborted the re-run before it could reset the module state this
 * file depends on surviving. Correct behaviour resting on an exception is a
 * trap for whoever edits this next.
 *
 * The guard makes re-execution a deliberate no-op: the element is already
 * registered, so the block is skipped and the first run's closure — the
 * pending swap and the timer ids — carries through the swap intact.
 */
if (!customElements.get('kp-sibling-row')) {
  (() => {
    /** How long a swap may take before the loader is worth showing. Under this,
     *  a spinner would appear and vanish, which is worse than none. */
    const KP_SIBLING_LOADER_DELAY = 320;

    /** Set by the click handler, read by the next row's connectedCallback — which
     *  Dawn runs synchronously while inserting the new DOM, before its own
     *  focus() call. Strings only: see the file header. */
    let kpPendingSwap = null;

    /** Timer ids, at module level rather than on the instance, because the row
     *  that SETS them is not the row that needs to CLEAR them: a successful swap
     *  replaces the row, and the outgoing one is not removed (and so not
     *  disconnected) until 500ms later. Instance state would leave the loader
     *  timer to fire on a node nobody can see, and would leave the fail-safe
     *  running for twelve seconds after the swap it was guarding had landed.
     *  Numbers, not element references — see the file header. */
    let kpLoaderTimer = null;
    let kpFailSafeTimer = null;

    /** Deliberately NOT cleared by kpClearSwapTimers: this one outlives the
     *  swap that started it, waiting to see whether Shopify reports the view
     *  itself. See reportProductView. */
    let kpViewReportTimer = null;

    function kpClearSwapTimers() {
      clearTimeout(kpLoaderTimer);
      clearTimeout(kpFailSafeTimer);
      kpLoaderTimer = null;
      kpFailSafeTimer = null;
    }

    /** The live region has to outlive the region whose destruction its own action
     *  caused: Dawn replaces <main>, and a live region inserted with its text
     *  already in it does not announce. So it lives on <body>, created once. */
    function kpSiblingStatusRegion() {
      let region = document.getElementById('KpSiblingStatus');
      if (region) return region;

      region = document.createElement('p');
      region.id = 'KpSiblingStatus';
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      document.body.appendChild(region);
      return region;
    }

    class KpSiblingRow extends HTMLElement {
      connectedCallback() {
        this.onClick = this.handleClick.bind(this);
        this.addEventListener('click', this.onClick);

        // Created empty on every page load, so it is already in the accessibility
        // tree by the time a swap writes into it.
        kpSiblingStatusRegion();

        if (kpPendingSwap) this.completeSwap();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
      }

      get swapEnabled() {
        return this.dataset.swap !== 'false';
      }

      handleClick(event) {
        const card = event.target.closest('.kp-siblings__card');
        if (!card || !this.contains(card)) return;

        // Modified clicks are the shopper asking for a new tab or a download. Let
        // the anchor be an anchor.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

        // The card for the product already on screen. Nothing to swap to, and
        // letting it navigate would reload the page the shopper is on.
        if (card.getAttribute('aria-current') === 'true') {
          event.preventDefault();
          return;
        }

        if (!this.swapEnabled) return;

        // One swap at a time. Three taps in a second must not queue three
        // competing requests.
        if (this.hasAttribute('data-busy')) {
          event.preventDefault();
          return;
        }

        // The safety net from ADR 0003. If a future Dawn renames the channel or
        // drops the swap path, we return WITHOUT preventDefault and the click
        // becomes the ordinary navigation the anchor always was. That degrades a
        // broken trigger from "the row is dead" to "the row navigates".
        const canPublish =
          typeof publish === 'function' &&
          typeof PUB_SUB_EVENTS === 'object' &&
          PUB_SUB_EVENTS?.optionValueSelectionChange &&
          card.dataset.productUrl &&
          document.querySelector('product-info');
        if (!canPublish) return;

        event.preventDefault();
        this.startSwap(card);
      }

      startSwap(card) {
        this.setAttribute('data-busy', '');

        // Immediately, with no delay: the shopper's finger is on this card and
        // the feedback has to land where it landed. The loader is the part that
        // waits.
        //
        // It is also a CLAIM about what page you are on, made before the page
        // has agreed. Remember which card was really current so a swap that
        // never lands can take the claim back — otherwise the row spends the
        // rest of the session pointing at a design the page is not showing,
        // and no card carries aria-current, which is what handleClick uses to
        // recognise "you are already here".
        this.restoreId = this.querySelector('[aria-current="true"]')?.id || null;
        this.querySelectorAll('.kp-siblings__card--selected').forEach((selected) => {
          selected.classList.remove('kp-siblings__card--selected');
          selected.removeAttribute('aria-current');
        });
        card.classList.add('kp-siblings__card--selected');

        kpClearSwapTimers();
        kpLoaderTimer = setTimeout(() => card.setAttribute('data-pending', ''), KP_SIBLING_LOADER_DELAY);

        // The scroll starts HERE, on the click, not after the swap lands.
        //
        // Measured at 390x808: running it afterwards produced CLS 0.67, and
        // 96% of that was one shift — the footer moving 238px while the
        // smooth scroll was passing over it. The page is not stable after a
        // swap: it loses 641px about a second later as the re-run scripts
        // collapse the reviews (-393), the FAQ (-187) and the description
        // (-62) back down. Animating the viewport across a layout that is
        // still resizing is what turns those into visible jank.
        //
        // Scrolling now inverts the problem. The layout under this scroll is
        // the OLD page, which finished settling long ago, so the animation is
        // smooth; and by the time the new DOM arrives the shopper is at the
        // top of the document, which puts every one of those height changes
        // BELOW the viewport, where a layout shift is neither seen nor
        // counted. It also reads the way a navigation should — the page
        // travels up to the gallery from where you tapped, instead of being
        // yanked from the footer.
        this.scrollToGallery();

        kpPendingSwap = {
          targetId: card.id,
          url: card.dataset.productUrl,
          name: card.dataset.name || '',
          announcement: this.dataset.announcement || '',
        };

        // Dawn's ProductInfo.handleOptionValueChange opens with
        // `if (!this.contains(event.target)) return;`, which is why the row is a
        // block on the product section rather than a section of its own.
        publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: {
            event: { target: card },
            target: card,
            selectedOptionValues: [],
          },
        });

        // A swap that never lands leaves the row locked and a loader spinning, so
        // the state is released either way. Dawn aborts and re-fetches on its own;
        // this only clears OUR pending marks, it cannot cancel Dawn's request.
        kpFailSafeTimer = setTimeout(() => this.clearPending(), 12000);
      }

      /**
       * Called only when a swap did NOT land — the twelve-second fail-safe.
       * A successful swap never runs this: the row it would clean up has been
       * replaced, and completeSwap clears the timers instead.
       */
      clearPending() {
        kpClearSwapTimers();
        this.removeAttribute('data-busy');
        this.querySelectorAll('[data-pending]').forEach((card) => card.removeAttribute('data-pending'));
        kpPendingSwap = null;

        // Put the selection back where the page actually is. Nothing arrived,
        // so the optimistic move made in startSwap was wrong.
        const restore = this.restoreId && this.querySelector(`#${CSS.escape(this.restoreId)}`);
        if (!restore) return;
        this.querySelectorAll('.kp-siblings__card--selected').forEach((selected) => {
          selected.classList.remove('kp-siblings__card--selected');
        });
        restore.classList.add('kp-siblings__card--selected');
        restore.setAttribute('aria-current', 'true');
      }

      /**
       * Runs on the NEW row, from connectedCallback, while Dawn is still inside
       * HTMLUpdateUtility.viewTransition — synchronously, before Dawn's own
       * `document.querySelector('#' + targetId)?.focus()`. That ordering is what
       * makes the focus patch below possible.
       */
      completeSwap() {
        const swap = kpPendingSwap;
        kpPendingSwap = null;

        // The swap has landed, so the loader must not appear now. This is what
        // makes "no spinner on a fast connection" true rather than merely
        // invisible: without it the timer still fires, on the outgoing node, and
        // the only reason nothing shows is that Dawn happens to have hidden it.
        kpClearSwapTimers();

        const card = this.querySelector(`#${CSS.escape(swap.targetId)}`);

        // Dawn is about to call focus() on this card. An unsuppressed focus drags
        // the viewport back down to the row and fights the gallery scroll below,
        // and which one wins is browser-dependent. Patching focus on this one
        // element is how the scroll is suppressed without editing product-info.js.
        // The node is discarded on the next swap, so the patch goes with it.
        if (card) {
          card.focus = function focusWithoutScrolling(options) {
            HTMLElement.prototype.focus.call(this, { ...options, preventScroll: true });
          };
        }

        // Everything below wants the new DOM settled and Dawn's focus() done, so
        // it waits a frame. Nothing captured here is an element reference held
        // across a swap — `card` is looked up again, in the DOM that exists now.
        requestAnimationFrame(() => {
          this.patchHead(swap.url);
          this.reportProductView();
          this.announce(swap);
          this.settleAtGallery();
          this.revealSelected();
        });
      }

      /**
       * Dawn copies the document title and rewrites history, and leaves the
       * rest of <head> describing the product the shopper started on. On a
       * store where each design is separately indexed and separately
       * advertised, an address bar that disagrees with the head undoes part of
       * the SEO work — a shared link would carry the new URL under the old
       * design's name, picture and price.
       *
       * ONE SOURCE for all of it: the Product JSON-LD the swap brought in.
       * Liquid rendered that for the new product, it is already the block
       * search engines read, and taking the head's values from it is what
       * stops the head and the structured data from disagreeing. Structured
       * data itself needs no patch — it lives inside <main> and is replaced
       * with everything else.
       *
       * Runs on the swap path only. A normal page load never reaches it, so
       * the server-rendered head is untouched.
       */
      patchHead(url) {
        const canonical = document.querySelector('link[rel="canonical"]');

        // The origin comes from the canonical Liquid already rendered, not
        // from `location`. A shopper can arrive on the .myshopify.com domain
        // while the store's canonical host is kyperus.com, and rebuilding the
        // URL from where they happen to be would rewrite a correct canonical
        // into a wrong one. Only the path is ours to change.
        const origin = canonical?.href || window.shopUrl || window.location.origin;
        const absolute = new URL(url, origin).href;

        if (canonical) canonical.href = absolute;
        this.setMeta('property', 'og:url', absolute);

        const product = this.swappedProductLd();
        if (!product) return;

        const image = Array.isArray(product.image) ? product.image[0] : product.image;
        if (image) {
          this.setMeta('property', 'og:image', image);
          this.setMeta('property', 'og:image:secure_url', image);

          // REMOVED, not updated. meta-tags.liquid emits width and height from
          // the Liquid image object, and nothing in the swapped-in DOM knows
          // the new picture's true pixel dimensions — so the only honest
          // options are to state nothing or to state something false. They are
          // optional hints; a crawler reads the image itself. Leaving the old
          // image's numbers beside a new image is an inconsistency this patch
          // would have introduced on its own.
          document
            .querySelectorAll('meta[property="og:image:width"], meta[property="og:image:height"]')
            .forEach((meta) => meta.remove());
        }

        if (product.name) {
          this.setMeta('property', 'og:title', product.name);
          this.setMeta('name', 'twitter:title', product.name);
        }

        // The structured data's description, which is the product description.
        // If the merchant has written a separate SEO description the server
        // used that instead, so this can change WHICH description is
        // advertised — but it describes the cue on screen, and the alternative
        // is a correct-looking description of a different product.
        if (product.description) {
          this.setMeta('property', 'og:description', product.description);
          this.setMeta('name', 'twitter:description', product.description);
        }

        const offer = Array.isArray(product.offers) ? product.offers[0] : product.offers;
        if (offer?.price) this.setMeta('property', 'og:price:amount', offer.price);
      }

      /** The swapped-in page's own Product node — the one with a name, so the
       *  spec-sheet node that repeats the @id is not mistaken for it. */
      swappedProductLd() {
        const blocks = document.querySelectorAll('script[type="application/ld+json"]');
        for (const block of blocks) {
          try {
            const parsed = JSON.parse(block.textContent);
            if (parsed && parsed['@type'] === 'Product' && parsed.name) return parsed;
          } catch {
            // A malformed block is not this feature's problem to report.
          }
        }
        return null;
      }

      setMeta(attribute, key, value) {
        document.querySelectorAll(`meta[${attribute}="${key}"]`).forEach((meta) => {
          meta.setAttribute('content', value);
        });
      }

      /**
       * A history replacement fires no page view, so without this a shopper
       * who compares three designs can be recorded as having viewed one, and
       * retargeting only ever sees the entry point.
       *
       * BUT WE ARE THE FALLBACK, NOT THE REPORTER. Shopify's own
       * <product-component> (registered in theme.liquid with
       * `defaultTrigger: 'intersect'`) does re-fire on a swap — but only when
       * the swapped-in element is in the viewport as it connects. Measured: it
       * dispatched 19ms after us. Since the row now scrolls to the gallery on
       * the click, the shopper is at the top of the document by the time the
       * new DOM lands, so in practice it almost always fires and a
       * second event of ours would double-count every swap.
       *
       * "Almost always" is not "always" — a page with no gallery to scroll to,
       * or a browser that refused the scroll, leaves it below the fold and
       * silent. So we wait, watch, and only speak if nobody else did. An
       * unreliable analytics signal is worse than none; a doubled one is
       * worse still, because it looks healthy.
       *
       * Reached only from completeSwap, which runs only when the new row
       * connects — so a failed or aborted swap reports nothing either way.
       */
      reportProductView() {
        const { ProductViewEvent } = window.StandardEvents || {};
        const component = document.querySelector('product-component[view-event-payload]');
        if (!ProductViewEvent || !component) return;

        let payload;
        try {
          payload = JSON.parse(component.getAttribute('view-event-payload'));
        } catch {
          return;
        }

        const handle = payload?.product?.handle;
        let reportedByShopify = false;
        const listen = (event) => {
          let seen;
          try {
            seen = (event.detail ?? event)?.product?.handle;
          } catch {
            seen = undefined;
          }
          if (!handle || seen === handle) reportedByShopify = true;
        };

        document.addEventListener('shopify:product:view', listen, true);
        clearTimeout(kpViewReportTimer);
        kpViewReportTimer = setTimeout(() => {
          document.removeEventListener('shopify:product:view', listen, true);

          // A newer swap has moved on; that swap reports for itself.
          if (handle && !window.location.pathname.endsWith(`/${handle}`)) return;
          if (reportedByShopify) return;

          component.dispatchEvent(new ProductViewEvent(payload));
        }, 600);
      }

      /** Status only. A failed swap is not announced here — the region carries
       *  what happened, not what went wrong. */
      announce({ name, announcement }) {
        if (!name || !announcement) return;
        kpSiblingStatusRegion().textContent = announcement.replace('__NAME__', name);
      }

      /**
       * The whole page content was replaced, so the interaction is semantically a
       * navigation, and a navigation moves the viewport. Without this the shopper
       * is left staring at the row they clicked while the cue they asked for sits
       * above the fold line they cannot see.
       */
      /**
       * The whole page content is being replaced, so the interaction is
       * semantically a navigation, and a navigation moves the viewport.
       * Called on the click — see the note in startSwap for why the timing is
       * the whole point.
       */
      scrollToGallery() {
        const gallery = document.querySelector('.product__media-wrapper, media-gallery');
        if (!gallery) return;

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        gallery.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      }

      /**
       * After the swap, a correction rather than a journey.
       *
       * The scroll started on the click usually has the shopper at the
       * gallery already, and re-animating from there is a second movement
       * that says nothing. This only acts when they are NOT there — a swap
       * that beat the animation, or a browser that ignored it — and it jumps
       * rather than glides, because at this moment the page is still losing
       * height underneath and a smooth scroll would ride that.
       */
      settleAtGallery() {
        const gallery = document.querySelector('.product__media-wrapper, media-gallery');
        if (!gallery) return;

        // Anything inside a screen of the top counts as arrived.
        if (Math.abs(gallery.getBoundingClientRect().top) < window.innerHeight * 0.5) return;
        gallery.scrollIntoView({ behavior: 'auto', block: 'start' });
      }

      /**
       * The row is rebuilt from the new product's data and its scroll offset
       * resets to zero, so a card chosen from the right-hand end of a longer
       * series lands off-screen.
       *
       * Done by arithmetic on the scroller rather than scrollIntoView, because
       * scrollIntoView has no way to say "horizontally only" — `block: 'nearest'`
       * still scrolls the page when the row is near an edge, which is the gallery
       * scroll above being fought for a second time.
       */
      revealSelected() {
        const list = this.querySelector('[data-kp-siblings-list]');
        const selected = this.querySelector('.kp-siblings__card--selected');
        if (!list || !selected || list.scrollWidth <= list.clientWidth) return;

        const item = selected.closest('.kp-siblings__item') || selected;
        const listBox = list.getBoundingClientRect();
        const itemBox = item.getBoundingClientRect();

        // Already fully in view: leave it alone. A row that shuffles itself on
        // every swap, including the ones that needed no movement, reads as a
        // glitch rather than as help.
        if (itemBox.left >= listBox.left && itemBox.right <= listBox.right) return;

        // Aligned to the start edge, which is where scroll-snap-align puts its
        // snap points on the phone. Centring the card would land between two of
        // them and the browser would correct it — a second, visible jump.
        //
        // scroll-margin-inline has to come out of the sum for the same reason:
        // it widens the card's snap area, so the real snap position is that
        // many pixels EARLIER than the card's left edge. Landing on the edge
        // itself is landing 12px past the snap point, and mandatory snapping
        // pulls it back — reintroducing exactly the jump the paragraph above
        // is avoiding. Read from the computed style so the two cannot drift.
        const snapInset = parseFloat(getComputedStyle(selected).scrollMarginInlineStart) || 0;
        const target = list.scrollLeft + (itemBox.left - listBox.left) - snapInset;
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        list.scrollTo({
          left: Math.max(0, target),
          behavior: reduced ? 'auto' : 'smooth',
        });
      }
    }

    customElements.define('kp-sibling-row', KpSiblingRow);
  })();
}
